import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import { ErrorField } from '@/components/error-field'
import * as Input from '@/components/input'
import { RegisterPreserverFormValues } from '@/schemas/auth-schema'
import api from '@/services/api'
import { normalizeCnpj, normalizeCpf, normalizePhone } from '@/utils/mask'

import { ModalTerms } from '../modal-terms/modal-terms'

interface PreserverFormProps {
  handleUpdateValueTermsModal: (value: boolean) => void
}

export function PreserverForm({
  handleUpdateValueTermsModal,
}: PreserverFormProps) {
  const { register, handleSubmit, formState, watch, setValue } =
    useFormContext<RegisterPreserverFormValues>()

  const [showPassword, setShowPassword] = useState(false)
  const [terms, setTerms] = useState(false)

  const registrationType = watch('tp_cadastro')
  const document = watch('documento')
  const phone = watch('phone')
  const isPhysical = registrationType === 'F'

  useEffect(() => {
    if (document === '' || document === undefined) return
    setValue(
      'documento',
      isPhysical ? normalizeCpf(document) : normalizeCnpj(document),
    )
  }, [document, isPhysical, setValue])

  useEffect(() => {
    if (phone === '' || phone === undefined) return
    setValue('phone', normalizePhone(phone))
  }, [phone, setValue])

  function showAndHidePassword() {
    setShowPassword(!showPassword)
  }

  async function handleRegister(data: RegisterPreserverFormValues) {
    const formData = { ...data, termos_aceitos: terms }
    await api
      .post('/user', formData)
      .then(() => handleUpdateValueTermsModal(true))
      .catch((error) =>
        toast.error(error.response.data.message, {
          className: 'register-preserver-error',
        }),
      )
  }

  return (
    <form onSubmit={handleSubmit(handleRegister)} className="space-y-4">
      <div className="mt-5">
        <Input.Label
          mandatory
          className="text-2xl"
          data-testid="register-preserver-type-label"
        >
          Tipo de cadastro
        </Input.Label>

        <div className="flex gap-10">
          <div className="flex items-center gap-2">
            <input
              id="physical"
              type="radio"
              value="F"
              data-testid="register-preserver-type-physical"
              {...register('tp_cadastro')}
            />
            <label htmlFor="physical">Física</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="legal"
              type="radio"
              value="P"
              data-testid="register-preserver-type-legal"
              {...register('tp_cadastro')}
            />
            <label htmlFor="legal">Jurídica</label>
          </div>
        </div>
      </div>

      <div>
        <Input.Label htmlFor="name" mandatory>
          {isPhysical ? 'Nome completo' : 'Nome da empresa'}
        </Input.Label>
        <Input.Root withError={!!formState.errors.nome}>
          <Input.Control
            id="nome"
            type="text"
            placeholder={
              isPhysical
                ? 'Digite seu nome completo'
                : 'Digite o nome da empresa'
            }
            data-testid="register-preserver-name-input"
            {...register('nome')}
          />
        </Input.Root>
        {formState.errors.nome && (
          <ErrorField>{formState.errors.nome.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="phone" mandatory>
          Telefone
        </Input.Label>
        <Input.Root withError={!!formState.errors.phone}>
          <Input.Control
            id="phone"
            type="text"
            maxLength={15}
            placeholder="(  ) _____-____"
            data-testid="register-preserver-phone-input"
            {...register('phone')}
          />
        </Input.Root>
        {formState.errors.phone && (
          <ErrorField>{formState.errors.phone.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="document" mandatory>
          {isPhysical ? 'CPF' : 'CNPJ'}
        </Input.Label>
        <Input.Root withError={!!formState.errors.documento}>
          <Input.Control
            id="document"
            type="text"
            maxLength={isPhysical ? 14 : 18}
            placeholder={
              isPhysical ? '000.000.000-00' : 'Digite o CNPJ da empresa'
            }
            data-testid="register-preserver-document-input"
            {...register('documento')}
          />
        </Input.Root>
        {formState.errors.documento && (
          <ErrorField>{formState.errors.documento.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="email" mandatory>
          E-mail
        </Input.Label>
        <Input.Root withError={!!formState.errors.email}>
          <Input.Control
            id="email"
            type="text"
            placeholder="Digite seu email"
            data-testid="register-preserver-email-input"
            {...register('email')}
          />
        </Input.Root>
        {formState.errors.email && (
          <ErrorField>{formState.errors.email.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="password" mandatory>
          Senha
        </Input.Label>
        <Input.Root withError={!!formState.errors.password}>
          <Input.Control
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Digite sua senha"
            data-testid="register-preserver-password-input"
            {...register('password')}
          />
          <Input.Prefix
            onClick={showAndHidePassword}
            className="cursor-pointer text-zinc-500"
            data-testid="register-preserver-password-toggle"
          >
            {showPassword ? <Eye /> : <EyeOff />}
          </Input.Prefix>
        </Input.Root>
        {formState.errors.password && (
          <ErrorField>{formState.errors.password.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="confirmPassword" mandatory>
          Confirme sua senha
        </Input.Label>
        <Input.Root withError={!!formState.errors.password}>
          <Input.Control
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder="Digite sua senha"
            data-testid="register-preserver-confirm-password-input"
            {...register('confirmPassword')}
          />
          <Input.Prefix
            onClick={showAndHidePassword}
            className="cursor-pointer text-zinc-500"
            data-testid="register-preserver-confirm-password-toggle"
          >
            {showPassword ? <Eye /> : <EyeOff />}
          </Input.Prefix>
        </Input.Root>
        {formState.errors.confirmPassword && (
          <ErrorField>{formState.errors.confirmPassword.message}</ErrorField>
        )}
      </div>

      <Button
        className="w-full"
        disabled={formState.isSubmitting || terms === false}
        type="submit"
        isSubmitting={formState.isSubmitting}
        data-testid="register-preserver-submit-button"
      >
        Cadastrar
      </Button>

      <div className="flex items-center gap-4">
        <input
          type="checkbox"
          checked={terms}
          onChange={() => setTerms((prev) => !prev)}
          data-testid="register-preserver-terms-checkbox"
        />
        <p className="text-xs">
          Declaro que li e concordo com os
          <ModalTerms setAccepted={() => setTerms(true)} />
          da plataforma e condições dos
          <span className="ml-1 text-bios-primary">
            Contratos para prestação de Serviços Ambientais.
          </span>
        </p>
      </div>
    </form>
  )
}
